import { createContext, type ReactNode, useContext, useEffect, useState } from 'react'
import { apiClient } from '@/lib/api-client'

interface User {
  id: string
  email: string
  name: string | null
  emailVerified: boolean
}

export interface Account {
  id: string
  tier: 'free' | 'starter' | 'pro' | 'growth' | 'scale'
  polar_subscription_status: string | null
  billing_interval: string | null
  emails_sent_this_period: number
  period_start: string | null
  period_end: string | null
  overage_strikes: number
  overage_acknowledged: boolean
  onboarding_completed: boolean
  onboarding_steps: Record<string, boolean> | null
  sending_paused: boolean
  pause_reason: string | null
  suspension_level: number
  suspension_count: number
  suspension_expires_at: string | null
  appeal_status: string
  appeal_submitted_at: string | null
  throttle_percent: number | null
  created_at: string
  updated_at: string
}

interface AuthContextValue {
  user: User | null
  account: Account | null
  isLoading: boolean
  isAuthenticated: boolean
  login: (email: string, password: string) => Promise<void>
  signup: (name: string, email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  refetchAccount: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

/** Fetch account without triggering the 401 redirect loop */
async function fetchAccount(): Promise<{ user: User; account: Account } | null> {
  const baseUrl = import.meta.env.VITE_API_BASE_URL ?? '/api'
  try {
    const res = await fetch(`${baseUrl}/dashboard/account`, {
      credentials: 'include',
      headers: { Accept: 'application/json' },
    })
    if (!res.ok) return null
    return res.json() as Promise<{ user: User; account: Account }>
  } catch {
    return null
  }
}

export function AuthProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [user, setUser] = useState<User | null>(null)
  const [account, setAccount] = useState<Account | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    fetchAccount()
      .then((data) => {
        if (data) {
          setUser(data.user)
          setAccount(data.account)
        }
      })
      .finally(() => setIsLoading(false))
  }, [])

  async function login(email: string, password: string): Promise<void> {
    await apiClient.post('/auth/sign-in/email', { email, password })
    // Session cookie is set — kick off account fetch in the background.
    // The caller can navigate immediately; ProtectedRoute shows a spinner
    // while isLoading is true, then the dashboard renders once data arrives.
    setIsLoading(true)
    fetchAccount()
      .then((data) => {
        if (data) {
          setUser(data.user)
          setAccount(data.account)
        }
      })
      .finally(() => setIsLoading(false))
  }

  async function signup(name: string, email: string, password: string): Promise<void> {
    await apiClient.post('/auth/sign-up/email', { name, email, password })
    setIsLoading(true)
    fetchAccount()
      .then((data) => {
        if (data) {
          setUser(data.user)
          setAccount(data.account)
        }
      })
      .finally(() => setIsLoading(false))
  }

  async function logout(): Promise<void> {
    await apiClient.post('/auth/sign-out', {})
    setUser(null)
    setAccount(null)
  }

  async function refetchAccount(): Promise<void> {
    const data = await fetchAccount()
    if (data) {
      setUser(data.user)
      setAccount(data.account)
    }
  }

  return (
    <AuthContext
      value={{
        user,
        account,
        isLoading,
        isAuthenticated: !!user,
        login,
        signup,
        logout,
        refetchAccount,
      }}
    >
      {children}
    </AuthContext>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
